import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const PRIORITY_COLORS = {
  low: "text-zinc-500",
  medium: "text-blue-400",
  high: "text-amber-400",
  urgent: "text-red-400",
} as const;

export const PRIORITY_DOT_COLORS = {
  low: "bg-zinc-600",
  medium: "bg-blue-500",
  high: "bg-amber-500",
  urgent: "bg-red-500",
} as const;

export const TASK_STATUS_ORDER = ["inbox", "ready", "working", "decision", "review", "done"] as const;

export const TASK_STATUS_LABELS = {
  inbox: "Inbox",
  ready: "Ready",
  working: "Working",
  decision: "Needs Decision",
  review: "Review",
  done: "Done",
} as const;

export const TASK_STATUS_HELP = {
  inbox: "Needs triage or clarification.",
  ready: "Approved and waiting for someone to pick it up.",
  working: "Actively being worked right now.",
  decision: "Waiting for a human decision before the agent can continue.",
  review: "Work is done and waiting for review.",
  done: "Completed and closed.",
} as const;

/**
 * Card/entity state. The six task statuses plus the two cross-cutting flags
 * that get their own accent: `blocked` (blockedReason set) and `waiting`
 * (stale, or blocked purely by an unfinished dependency).
 */
export type StatusKey =
  | (typeof TASK_STATUS_ORDER)[number]
  | "blocked"
  | "waiting";

export const STATUS_KEY_LABELS: Record<StatusKey, string> = {
  ...TASK_STATUS_LABELS,
  blocked: "Blocked",
  waiting: "Waiting",
};

/**
 * Resolve the single accent a card should wear. Blocked/stale outrank the
 * column the task sits in, since those are what the eye needs to find first.
 */
export function statusKeyFor(
  status: (typeof TASK_STATUS_ORDER)[number],
  opts: { blocked?: boolean; autoBlocked?: boolean; stale?: boolean } = {}
): StatusKey {
  if (opts.autoBlocked) return "waiting";
  if (opts.blocked) return "blocked";
  if (opts.stale) return "waiting";
  return status;
}

/** Left accent bar. Pair with `border-l-4` on a neutral card. */
export const STATUS_ACCENT: Record<StatusKey, string> = {
  inbox: "border-l-status-inbox",
  ready: "border-l-status-ready",
  working: "border-l-status-working",
  decision: "border-l-status-decision",
  review: "border-l-status-review",
  done: "border-l-status-done",
  blocked: "border-l-status-blocked",
  waiting: "border-l-status-waiting",
};

/** Solid dot / bar fill in the state color. */
export const STATUS_DOT: Record<StatusKey, string> = {
  inbox: "bg-status-inbox",
  ready: "bg-status-ready",
  working: "bg-status-working",
  decision: "bg-status-decision",
  review: "bg-status-review",
  done: "bg-status-done",
  blocked: "bg-status-blocked",
  waiting: "bg-status-waiting",
};

/** Text-only accent, for icons and inline labels on a neutral background. */
export const STATUS_TEXT: Record<StatusKey, string> = {
  inbox: "text-status-inbox-fg",
  ready: "text-status-ready-fg",
  working: "text-status-working-fg",
  decision: "text-status-decision-fg",
  review: "text-status-review-fg",
  done: "text-status-done-fg",
  blocked: "text-status-blocked-fg",
  waiting: "text-status-waiting-fg",
};

/**
 * The one status pill per card: a low-opacity tint of the accent with the
 * tuned foreground on top. Dark mode gets a heavier tint because the accent
 * has less to push against on near-black.
 */
export const STATUS_PILL: Record<StatusKey, string> = {
  inbox:
    "bg-status-inbox/15 text-status-inbox-fg dark:bg-status-inbox/20",
  ready:
    "bg-status-ready/15 text-status-ready-fg dark:bg-status-ready/25",
  working:
    "bg-status-working/15 text-status-working-fg dark:bg-status-working/25",
  decision:
    "bg-status-decision/15 text-status-decision-fg dark:bg-status-decision/20",
  review:
    "bg-status-review/15 text-status-review-fg dark:bg-status-review/25",
  done:
    "bg-status-done/15 text-status-done-fg dark:bg-status-done/25",
  blocked:
    "bg-status-blocked/15 text-status-blocked-fg dark:bg-status-blocked/25",
  waiting:
    "bg-status-waiting/15 text-status-waiting-fg dark:bg-status-waiting/20",
};

/** Base geometry for a pill. Compose with STATUS_PILL. */
export const PILL_BASE =
  "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider";

/**
 * Retained for existing consumers that index by task status. Same shape as
 * before (one class string per status) — only the colors changed, and they
 * now carry light-mode variants.
 */
export const STATUS_COLORS = STATUS_PILL;

export const AGENT_STATUS_COLORS = {
  active: "bg-emerald-500",
  idle: "bg-zinc-500",
  blocked: "bg-red-500",
  waiting: "bg-amber-500",
  inactive: "bg-zinc-800",
} as const;

export const AGENT_STATUS_LABELS = {
  active: "Active",
  idle: "Idle",
  blocked: "Blocked",
  waiting: "Waiting",
  inactive: "Inactive",
} as const;

export function isStale(updatedAt: number, status: string): boolean {
  if (status !== "working") return false;
  const threeDaysAgo = Date.now() - 3 * 86400000;
  return updatedAt < threeDaysAgo;
}

export function formatRelativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatCron(expr: string): string {
  const parts = expr.split(" ");
  if (parts.length !== 5) return expr;
  const [min, hour, , , dow] = parts;

  if (dow === "1-5") {
    return `Weekdays at ${hour}:${min.padStart(2, "0")}`;
  }
  if (dow === "1") {
    return `Every Monday at ${hour}:${min.padStart(2, "0")}`;
  }
  if (dow === "3") {
    return `Every Wednesday at ${hour}:${min.padStart(2, "0")}`;
  }
  if (dow === "5") {
    return `Every Friday at ${hour}:${min.padStart(2, "0")}`;
  }
  return expr;
}
